import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, RefreshCw, AlertTriangle, Copy, Check, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { productsService, ActiveFlyerProduct, SyncSoldOutResult, SyncSoldOutProduct } from '../../services/productsService';
import { flyersService } from '../../services/flyersService';

type Filter = 'all' | 'active' | 'discontinued' | 'soldOut';

// Zmena stavu jednoho produktu v ramci jednoho letaku.
type Smer = 'soldOut' | 'zpet';
type Zmena = { produkt: string; ean: string; smer: Smer };
// Seskupeno podle LETAKU - pregenerovani PDF se dela po letacich a jeden letak
// muze mit vice zmenenych produktu.
type Skupina = {
  id: string;
  nazev: string;
  validFrom?: string;
  validTo?: string;
  dodavatel: string;
  email: string;
  zmeny: Zmena[];
};
type StavPdf = { stav: 'ceka' | 'probiha' | 'hotovo' | 'chyba'; chyba?: string };

const den = (d?: string) => (d ? new Date(d).toLocaleDateString('cs-CZ') : '');

const sestavSkupiny = (res: SyncSoldOutResult): Skupina[] => {
  const mapa = new Map<string, Skupina>();
  const pridej = (produkty: SyncSoldOutProduct[] | undefined, smer: Smer) => {
    (produkty || []).forEach((p) => {
      (p.flyers || []).forEach((f) => {
        if (!mapa.has(f.id)) {
          mapa.set(f.id, {
            id: f.id,
            nazev: f.name,
            validFrom: f.validFrom,
            validTo: f.validTo,
            dodavatel: f.supplierName || 'neznámý dodavatel',
            email: f.supplierEmail || '',
            zmeny: [],
          });
        }
        mapa.get(f.id)!.zmeny.push({ produkt: p.name, ean: p.eanCode, smer });
      });
    });
  };
  pridej(res.discontinuedProducts, 'soldOut');
  pridej(res.reactivatedProducts, 'zpet');
  return Array.from(mapa.values());
};

// Text je psany jako zprava PRO DODAVATELE - informuje o zmene stavu produktu
// a o tom, ze PDF letaku uz bylo (nebo jeste nebylo) pregenerovano.
const textProLetak = (s: Skupina, stav: StavPdf['stav']): string => {
  const r: string[] = [];
  r.push(`Změna stavu produktů v letáku (${new Date().toLocaleDateString('cs-CZ')})`);
  r.push('');
  r.push(`Leták: ${s.nazev}${s.validFrom ? ` (platnost ${den(s.validFrom)} – ${den(s.validTo)})` : ''}`);
  r.push(`Dodavatel: ${s.dodavatel}${s.email ? ` <${s.email}>` : ''}`);
  r.push('');
  r.push('Podle ERP se u produktů ve vašem aktivním letáku změnil stav:');
  s.zmeny.forEach((z) =>
    r.push(
      `  • ${z.produkt} (${z.ean}) – ${
        z.smer === 'soldOut'
          ? 'nově označeno jako VYPRODÁNO'
          : 'označení VYPRODÁNO zrušeno (produkt je zpět v ERP)'
      }`
    )
  );
  r.push('');
  r.push(
    stav === 'hotovo'
      ? 'PDF letáku jsme kvůli této změně přegenerovali – v aplikaci je už aktuální verze s odpovídajícím vodoznakem VYPRODÁNO. Ceny ani obsah letáku se přegenerováním nemění.'
      : 'PDF letáku je potřeba kvůli této změně přegenerovat – v uloženém PDF je zatím ještě původní stav. Ceny ani obsah letáku se přegenerováním nemění.'
  );
  return r.join('\n');
};

const sestavCelyText = (skupiny: Skupina[], stavy: Record<string, StavPdf>): string =>
  skupiny.map((s) => textProLetak(s, stavy[s.id]?.stav ?? 'ceka')).join('\n\n' + '─'.repeat(60) + '\n\n');

export const ActiveFlyersProductsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  // Vysledek synchronizace se ukazuje v okne s vybratelnym textem - z alert()
  // se text zkopirovat neda a schvalovatel ho potrebuje poslat dodavateli.
  const [vysledek, setVysledek] = useState<SyncSoldOutResult | null>(null);
  const [stavPdf, setStavPdf] = useState<Record<string, StavPdf>>({});
  const [zkopirovano, setZkopirovano] = useState<string | null>(null);
  // Zprava pro dodavatele je u kazdeho letaku vlastni. U jednoho letaku se ukaze
  // rovnou, u vice se sbali, aby se okno veslo na obrazovku.
  const [rozbalene, setRozbalene] = useState<Record<string, boolean>>({});

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['active-flyers-products'],
    queryFn: () => productsService.getActiveFlyersProducts(),
  });

  const markMutation = useMutation({
    mutationFn: () => productsService.markDiscontinuedAsSoldOut(),
    onSuccess: (res) => {
      setVysledek(res);
      setStavPdf({});
      setZkopirovano(null);
      setRozbalene({});
      queryClient.invalidateQueries({ queryKey: ['active-flyers-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (e: any) => alert(e.response?.data?.message || 'Chyba při označování produktů'),
  });

  const skupiny = vysledek ? sestavSkupiny(vysledek) : [];
  const zbyvaPregenerovat = skupiny.filter((s) => (stavPdf[s.id]?.stav ?? 'ceka') !== 'hotovo');
  const probihaNejake = skupiny.some((s) => stavPdf[s.id]?.stav === 'probiha');

  const pregeneruj = async (flyerId: string) => {
    setStavPdf((s) => ({ ...s, [flyerId]: { stav: 'probiha' } }));
    try {
      await flyersService.generatePdf(flyerId);
      setStavPdf((s) => ({ ...s, [flyerId]: { stav: 'hotovo' } }));
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
      queryClient.invalidateQueries({ queryKey: ['active-flyers'] });
    } catch (e: any) {
      setStavPdf((s) => ({
        ...s,
        [flyerId]: { stav: 'chyba', chyba: e.response?.data?.message || 'Přegenerování PDF se nezdařilo.' },
      }));
    }
  };

  // Schvalovatel si muze leták prohlédnout pred pregenerovanim i po nem - proto
  // vzdy s obchazenim cache, aby po pregenerovani nevyskocila stara verze.
  const zobrazPdf = async (flyerId: string) => {
    try {
      const blob = await flyersService.getPdfBlob(flyerId, true);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      alert('Chyba při zobrazení PDF');
    }
  };

  // Postupne, ne paralelne - generovani PDF je narocne a letaku muze byt vic.
  const pregenerujVse = async () => {
    for (const s of zbyvaPregenerovat) {
      // eslint-disable-next-line no-await-in-loop
      await pregeneruj(s.id);
    }
  };

  const kopiruj = async (text: string, klic: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setZkopirovano(klic);
      setTimeout(() => setZkopirovano(null), 2000);
    } catch {
      // Kdyby prohlizec schranku nepovolil, text je i tak vybratelny v textarea.
      alert('Zkopírování se nezdařilo – text lze označit a zkopírovat ručně.');
    }
  };

  const discontinuedCount = products.filter((p) => p.discontinued).length;
  const reactivatableCount = products.filter((p) => !p.discontinued && p.soldOut).length;
  const soldOutCount = products.filter((p) => p.soldOut).length;
  const canApply = discontinuedCount > 0 || reactivatableCount > 0;

  const filtered = products
    .filter((p) => {
      if (filter === 'active') return !p.discontinued;
      if (filter === 'discontinued') return p.discontinued;
      if (filter === 'soldOut') return p.soldOut;
      return true;
    })
    .filter((p) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        p.eanCode.toLowerCase().includes(q) ||
        p.brandName.toLowerCase().includes(q)
      );
    });

  const handleApply = () => {
    if (!canApply) {
      alert('Žádné změny k provedení');
      return;
    }
    const lines: string[] = [];
    if (discontinuedCount > 0) lines.push(`${discontinuedCount} ukončených produktů bude označeno jako vyprodáno`);
    if (reactivatableCount > 0) lines.push(`${reactivatableCount} produktů bude odznačeno (zpět v ERP)`);
    if (window.confirm(`${lines.join('\n')}\n\nPokračovat?`)) {
      markMutation.mutate();
    }
  };

  const money = (v: number | string | null) => (v == null ? '' : `${Number(v).toLocaleString('cs-CZ')} Kč`);

  const FILTERS: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: 'Vše', count: products.length },
    { key: 'active', label: 'Aktivní', count: products.length - discontinuedCount },
    { key: 'discontinued', label: 'Ukončené v ERP', count: discontinuedCount },
    { key: 'soldOut', label: 'Vyprodáno', count: soldOutCount },
  ];

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-gray-900">Produkty v aktivních letácích</h1>
          <button
            onClick={handleApply}
            disabled={markMutation.isPending || !canApply}
            className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${markMutation.isPending ? 'animate-spin' : ''}`} />
            Synchronizovat vyprodané dle ERP
          </button>
        </div>
        <p className="text-gray-600">
          Produkty použité v aktivních letácích. Ukončené v ERP lze hromadně označit jako vyprodané.
        </p>
      </div>

      {/* Filters + search */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              filter === f.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.label} ({f.count})
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat podle názvu, EAN, značky…"
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produkt</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Značka</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategorie</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cena</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Letáky</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stav</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((p: ActiveFlyerProduct) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{p.name}</div>
                  <div className="text-xs text-gray-500">{p.eanCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="inline-flex items-center gap-2 max-w-[14rem]">
                    {p.brandColor && (
                      <span className="w-3 h-3 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: p.brandColor }} />
                    )}
                    <span className="truncate" title={p.brandName}>{p.brandName}</span>
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.categoryName || '—'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {money(p.price)}
                  {p.originalPrice ? <span className="text-xs text-gray-400 line-through ml-1">{money(p.originalPrice)}</span> : null}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {p.flyers.map((f) => f.name).join(', ')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {p.discontinued && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Ukončeno v ERP
                      </span>
                    )}
                    {p.soldOut && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                        Vyprodáno
                      </span>
                    )}
                    {!p.discontinued && !p.soldOut && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Aktivní
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">Žádné produkty</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={!!vysledek} onClose={() => setVysledek(null)} title="Výsledek synchronizace" size="lg">
        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          <p className="text-sm text-gray-700">{vysledek?.message}</p>

          {skupiny.length === 0 ? (
            <p className="text-sm text-gray-500">Žádný aktivní leták změnou dotčen nebyl.</p>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-gray-800">
                    {skupiny.length === 1 ? 'Dotčen je 1 leták' : `Dotčené letáky: ${skupiny.length}`}
                  </span>{' '}
                  – u každého je vlastní zpráva pro dodavatele. Uložené PDF je snímek, vodoznak
                  VYPRODÁNO se v něm objeví (nebo zmizí) až po přegenerování.
                </p>
                {skupiny.length > 1 && zbyvaPregenerovat.length > 0 && (
                  <Button size="sm" onClick={pregenerujVse} disabled={probihaNejake} className="flex-shrink-0">
                    <RefreshCw className={`w-4 h-4 mr-2 ${probihaNejake ? 'animate-spin' : ''}`} />
                    Přegenerovat vše ({zbyvaPregenerovat.length})
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                {skupiny.map((s, idx) => {
                  const st = stavPdf[s.id]?.stav ?? 'ceka';
                  // U jednoho letaku ma smysl mit zpravu rovnou videt, u vice by
                  // se okno neunosne natahlo - proto sbalene s moznosti rozbalit.
                  const jeRozbalene = rozbalene[s.id] ?? skupiny.length === 1;
                  const text = textProLetak(s, st);
                  return (
                    <div key={s.id} className="border border-gray-200 rounded-lg">
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">
                                {idx + 1}/{skupiny.length}
                              </span>
                              <span className="font-medium text-gray-900 truncate" title={s.nazev}>{s.nazev}</span>
                            </div>
                            <div className="text-xs text-gray-500 truncate mt-0.5">
                              {s.dodavatel}
                              {s.email ? ` · ${s.email}` : ''}
                              {s.validFrom ? ` · platnost ${den(s.validFrom)} – ${den(s.validTo)}` : ''}
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => zobrazPdf(s.id)}>
                              <FileText className="w-4 h-4 mr-2" />
                              {st === 'hotovo' ? 'Otevřít nové PDF' : 'Otevřít PDF'}
                            </Button>
                            {st === 'hotovo' ? (
                              <span className="inline-flex items-center text-sm font-medium text-green-700">
                                <Check className="w-4 h-4 mr-1" />
                                Přegenerováno
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant={st === 'chyba' ? 'outline' : 'primary'}
                                onClick={() => pregeneruj(s.id)}
                                disabled={st === 'probiha'}
                              >
                                <RefreshCw className={`w-4 h-4 mr-2 ${st === 'probiha' ? 'animate-spin' : ''}`} />
                                {st === 'probiha' ? 'Generuji…' : st === 'chyba' ? 'Zkusit znovu' : 'Přegenerovat PDF'}
                              </Button>
                            )}
                          </div>
                        </div>

                        <ul className="mt-3 space-y-1">
                          {s.zmeny.map((z, i) => (
                            <li key={i} className="flex items-center gap-2 min-w-0 text-sm text-gray-700">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                                  z.smer === 'soldOut' ? 'bg-gray-200 text-gray-800' : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {z.smer === 'soldOut' ? 'Vyprodáno' : 'Zpět v prodeji'}
                              </span>
                              <span className="truncate" title={z.produkt}>{z.produkt}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0">{z.ean}</span>
                            </li>
                          ))}
                        </ul>

                        {stavPdf[s.id]?.chyba && (
                          <p className="mt-2 text-sm text-red-600">{stavPdf[s.id]!.chyba}</p>
                        )}
                      </div>

                      <div className="border-t border-gray-200 bg-gray-50 rounded-b-lg">
                        <div className="flex items-center justify-between gap-3 px-4 py-2">
                          <button
                            onClick={() => setRozbalene((r) => ({ ...r, [s.id]: !jeRozbalene }))}
                            className="flex items-center gap-1 min-w-0 text-sm font-medium text-gray-700 hover:text-gray-900"
                          >
                            {jeRozbalene ? (
                              <ChevronDown className="w-4 h-4 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-4 h-4 flex-shrink-0" />
                            )}
                            <span className="truncate">
                              Zpráva pro dodavatele{' '}
                              <span className="font-normal text-gray-500">
                                (po přegenerování PDF se text upraví)
                              </span>
                            </span>
                          </button>
                          <button
                            onClick={() => kopiruj(text, s.id)}
                            className="inline-flex items-center flex-shrink-0 text-sm text-blue-600 hover:text-blue-800"
                          >
                            {zkopirovano === s.id ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                            {zkopirovano === s.id ? 'Zkopírováno' : 'Kopírovat'}
                          </button>
                        </div>
                        {jeRozbalene && (
                          <div className="px-4 pb-3">
                            <textarea
                              readOnly
                              value={text}
                              onFocus={(e) => e.currentTarget.select()}
                              className="w-full h-44 p-3 text-sm font-mono border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            {skupiny.length > 1 && (
              <Button variant="outline" onClick={() => kopiruj(sestavCelyText(skupiny, stavPdf), 'vse')}>
                {zkopirovano === 'vse' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {zkopirovano === 'vse' ? 'Zkopírováno' : `Kopírovat všechny zprávy (${skupiny.length})`}
              </Button>
            )}
            <Button onClick={() => setVysledek(null)}>Zavřít</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
